// Judge.me's loader.js calls jdgm.loadScript for widget_write_review_modal.js as soon
// as it sees a review widget container, because its selector map ties that file to
// ".jdgm-review-widget, #judgeme_product_reviews, [data-widget=review]". The file is
// about 143 KB raw and only a shopper who decides to write a review needs it, so this
// gate holds that one request until intent is shown (DEC-210), and since DEC-216 holds it
// indefinitely, because the store collects reviews by email only. Measured on live
// 2026-09-19, the page both prefetches the file at idle priority and loads it as an
// executing script; only the executing load costs main-thread time, and the prefetch is
// deliberately left alone so a shopper who does open the form pays no extra latency.
//
// Fail-closed: if Judge.me renames loadScript, never defines its global, or drops the
// bookkeeping this wrapper has to carry, the gate does not install and the page behaves
// exactly as it does today. It never holds anything but the modal, and never re-closes.

const MODAL_PATTERN = /widget_write_review_modal|write_review_modal/i
const INTENT_SELECTOR = '.jdgm-write-rev-link, .jdgm-ask-question-btn, [data-write-review]'
// Judge.me deep-links straight to the form with these, so the modal is needed immediately.
const DEEP_LINK_PATTERN = /judgeme|jdgm/i

const install = () => {
  const jdgm = window.jdgm
  const original = jdgm?.loadScript
  if (typeof original !== 'function') return false
  // loader.js reads jdgm.loadScript.requestedUrls from inside the function it just
  // defined, and retries through jdgm.loadScript itself, so a wrapper that does not carry
  // that bookkeeping makes every Judge.me script load throw. Judge.me does the same
  // carry-over for loadCSS. Without a usable list there is nothing safe to wrap.
  if (!Array.isArray(original.requestedUrls)) return false

  let open = false
  let held = null

  // `replay` decides what happens to the request being held. Shopper intent, a deep link
  // and the backstop all replay it, because the form may be wanted now. Opening without a
  // replay is only for signals that mean Judge.me will ask again by itself.
  const release = ({ replay }) => {
    if (open) return
    open = true
    const pending = held
    held = null
    if (replay && pending) original.apply(jdgm, pending)
  }

  const gated = (...args) => {
    const url = String(args[0] ?? '')
    if (open || !MODAL_PATTERN.test(url)) return original.apply(jdgm, args)
    // Keep only the first held call; loader.js de-duplicates by URL itself.
    if (!held) held = args
    return undefined
  }

  for (const key of Object.getOwnPropertyNames(original)) {
    if (key === 'length' || key === 'name' || key === 'prototype') continue
    try {
      gated[key] = original[key]
    } catch {
      // A non-writable property cannot be carried; leave the loader's own value in place.
    }
  }

  jdgm.loadScript = gated

  // Shopper intent beats every other signal, and capture runs before Judge.me's handler.
  document.addEventListener('click', (event) => {
    if (event.target?.closest?.(INTENT_SELECTOR)) release({ replay: true })
  }, { capture: true })
  // A link into the reviews section means the form may be wanted straight away.
  if (DEEP_LINK_PATTERN.test(location.hash) || DEEP_LINK_PATTERN.test(location.search)) {
    release({ replay: true })
  }
  // No timed backstop (DEC-216): the store collects reviews by email only and the widget
  // shows no write-review control, so nothing on the page can open the form. The click and
  // deep-link releases above stay as fail-safes in case Judge.me ever renders one again.

  return true
}

if (!install()) {
  // loader.js defines jdgm.loadScript when it runs, so retry until it exists.
  let tries = 0
  const timer = setInterval(() => {
    if (install() || ++tries > 40) clearInterval(timer)
  }, 50)
  window.addEventListener('load', () => clearInterval(timer), { once: true })
}
